import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';

import TabTrapper from './TabTrapper';

interface ModalProps {
  children: React.ReactNode;
  className?: string;
  id?: string;
  isVisible?: boolean;
  hideModal?: () => void;
}

const Modal: React.FC<ModalProps> = ({ children, className, id, isVisible = false, hideModal }) => {
  const [bodyElement, setBodyElement] = useState<HTMLElement | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const modalWrapperRef = useRef<HTMLDivElement>(null);
  const prevIsVisibleRef = useRef<boolean>(isVisible);

  const handleEscPress = useCallback(
    (e: KeyboardEvent) => {
      if ((e.key === 'Escape' || e.which === 27) && hideModal) {
        hideModal();
      }
    },
    [hideModal]
  );

  const onAfterShowModal = useCallback(() => {
    // Wait one frame before focusing
    requestAnimationFrame(() => {
      modalRef.current?.focus();
      if (modalWrapperRef.current) {
        modalWrapperRef.current.scrollTop = 0;
      }
    });
  }, []);

  // Handle modal visibility changes
  useEffect(() => {
    if (isVisible && !prevIsVisibleRef.current) {
      onAfterShowModal();
    }
    prevIsVisibleRef.current = isVisible;
  }, [isVisible, onAfterShowModal]);

  // Mount effect
  useEffect(() => {
    setBodyElement(document.body);
    window.addEventListener('keyup', handleEscPress);

    return () => {
      window.removeEventListener('keyup', handleEscPress);
    };
  }, [handleEscPress]);

  const ariaProps = {
    'aria-modal': true as const,
    role: 'dialog' as const
  };

  if (!bodyElement) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      className={cn('modal', {
        'is-visible': isVisible
      })}
      id={id}
      ref={modalWrapperRef}
      suppressHydrationWarning={true}
      {...ariaProps}
    >
      <div className="modal-background" onClick={hideModal} />

      <div className={cn('modal-content', className)} ref={modalRef} tabIndex={-1}>
        <TabTrapper isActive={isVisible}>{children}</TabTrapper>
      </div>
    </div>,
    bodyElement
  );
};

Modal.displayName = 'Modal';

export default Modal;
